import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { Text } = Typography;

type HotelGuest = {
  id: number;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone?: string | null;
  gender?: string | null;
  date_of_birth?: string | null;
  nationality?: string | null;
  id_type?: string | null;
  id_number?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  postal_code?: string | null;
  notes?: string | null;
  preferences?: string | null;
  blacklisted?: number | boolean | null;
};

type GuestDocument = {
  id: number;
  document_type: string;
  document_number: string;
  issue_date?: string | null;
  expiry_date?: string | null;
  issuing_country?: string | null;
  document_file?: string | null;
  created_at?: string | null;
};

type GuestFormValues = {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  gender?: string;
  dateOfBirth?: Dayjs;
  nationality?: string;
  idType?: string;
  idNumber?: string;
  address?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  notes?: string;
  blacklisted?: boolean;
};

type DocumentFormValues = {
  documentType: string;
  documentNumber: string;
  issueDate?: Dayjs;
  expiryDate?: Dayjs;
  issuingCountry?: string;
  documentFile?: string;
};

const GuestsTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [guests, setGuests] = useState<HotelGuest[]>([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [filters, setFilters] = useState({
    search: "",
    blacklisted: "all",
  });
  const [isGuestModalOpen, setIsGuestModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<HotelGuest | null>(null);
  const [selectedGuest, setSelectedGuest] = useState<HotelGuest | null>(null);
  const [documents, setDocuments] = useState<GuestDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [guestForm] = Form.useForm<GuestFormValues>();
  const [documentForm] = Form.useForm<DocumentFormValues>();

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const canCreateGuest = Boolean(hasPermission?.("Hotellerie.Clients.create"));
  const canEditGuest = Boolean(hasPermission?.("Hotellerie.Clients.edit"));
  const canDeleteGuest = Boolean(hasPermission?.("Hotellerie.Clients.delete"));

  const fetchGuests = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingGuests(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.search.trim()) {
        params.search = filters.search.trim();
      }
      if (filters.blacklisted !== "all") {
        params.blacklisted = filters.blacklisted;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/guests`, {
        params,
      });
      if (response.data.success) {
        setGuests(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching guests:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des clients"
      );
    } finally {
      setLoadingGuests(false);
    }
  }, [filters.blacklisted, filters.search, requiresCompanySelection, selectedCompany]);

  const fetchDocuments = useCallback(
    async (guestId: number) => {
      if (requiresCompanySelection) {
        return;
      }
      setDocumentsLoading(true);
      try {
        const params = selectedCompany ? { company_id: selectedCompany } : {};
        const response = await axios.get(
          `${API_CONFIG.API_URL}/hotel/guests/${guestId}/documents`,
          { params }
        );
        if (response.data.success) {
          setDocuments(response.data.data || []);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching guest documents:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement des documents"
        );
      } finally {
        setDocumentsLoading(false);
      }
    },
    [requiresCompanySelection, selectedCompany]
  );

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  const openCreateGuest = () => {
    setEditingGuest(null);
    guestForm.resetFields();
    guestForm.setFieldsValue({
      blacklisted: false,
    });
    setIsGuestModalOpen(true);
  };

  const openEditGuest = (guest: HotelGuest) => {
    setEditingGuest(guest);
    guestForm.setFieldsValue({
      firstName: guest.first_name,
      lastName: guest.last_name,
      email: guest.email || undefined,
      phone: guest.phone || undefined,
      gender: guest.gender || undefined,
      dateOfBirth: guest.date_of_birth ? dayjs(guest.date_of_birth) : undefined,
      nationality: guest.nationality || undefined,
      idType: guest.id_type || undefined,
      idNumber: guest.id_number || undefined,
      address: guest.address || undefined,
      city: guest.city || undefined,
      country: guest.country || undefined,
      postalCode: guest.postal_code || undefined,
      notes: guest.notes || undefined,
      blacklisted: Boolean(guest.blacklisted),
    });
    setIsGuestModalOpen(true);
  };

  const handleSaveGuest = async () => {
    const values = await guestForm.validateFields();
    if (!editingGuest && !canCreateGuest) {
      message.warning("Vous n'avez pas les droits pour creer un client.");
      return;
    }
    if (editingGuest && !canEditGuest) {
      message.warning("Vous n'avez pas les droits pour modifier ce client.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      first_name: values.firstName,
      last_name: values.lastName,
      email: values.email || null,
      phone: values.phone || null,
      gender: values.gender || null,
      date_of_birth: values.dateOfBirth
        ? values.dateOfBirth.format("YYYY-MM-DD")
        : null,
      nationality: values.nationality || null,
      id_type: values.idType || null,
      id_number: values.idNumber || null,
      address: values.address || null,
      city: values.city || null,
      country: values.country || null,
      postal_code: values.postalCode || null,
      notes: values.notes || null,
      blacklisted: values.blacklisted ? 1 : 0,
    };

    try {
      if (editingGuest) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/guests/${editingGuest.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Client mis a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/guests`,
          payload
        );
        if (response.data.success) {
          message.success("Client cree.");
        }
      }
      setIsGuestModalOpen(false);
      setEditingGuest(null);
      fetchGuests();
    } catch (error: any) {
      console.error("[HOTEL] Error saving guest:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde du client"
      );
    }
  };

  const confirmDeleteGuest = (guest: HotelGuest) => {
    if (!canDeleteGuest) {
      message.warning("Vous n'avez pas les droits pour supprimer ce client.");
      return;
    }
    Modal.confirm({
      title: "Supprimer le client",
      content: `Confirmer la suppression de ${guest.first_name} ${guest.last_name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/guests/${guest.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Client supprime.");
            fetchGuests();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting guest:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression du client"
          );
        }
      },
    });
  };

  const openGuestDrawer = async (guest: HotelGuest) => {
    setSelectedGuest(guest);
    documentForm.resetFields();
    await fetchDocuments(guest.id);
  };

  const handleAddDocument = async () => {
    if (!selectedGuest) {
      return;
    }
    const values = await documentForm.validateFields();
    if (!canEditGuest) {
      message.warning("Vous n'avez pas les droits pour modifier ce client.");
      return;
    }

    try {
      const payload = {
        ...(selectedCompany ? { company_id: selectedCompany } : {}),
        document_type: values.documentType,
        document_number: values.documentNumber,
        issue_date: values.issueDate
          ? values.issueDate.format("YYYY-MM-DD")
          : null,
        expiry_date: values.expiryDate
          ? values.expiryDate.format("YYYY-MM-DD")
          : null,
        issuing_country: values.issuingCountry || null,
        document_file: values.documentFile || null,
      };
      const response = await axios.post(
        `${API_CONFIG.API_URL}/hotel/guests/${selectedGuest.id}/documents`,
        payload
      );
      if (response.data.success) {
        message.success("Document ajoute.");
        documentForm.resetFields();
        fetchDocuments(selectedGuest.id);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error creating document:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la creation du document"
      );
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!selectedGuest) {
      return;
    }
    if (!canEditGuest) {
      message.warning("Vous n'avez pas les droits pour modifier ce client.");
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.delete(
        `${API_CONFIG.API_URL}/hotel/guests/${selectedGuest.id}/documents/${documentId}`,
        { params }
      );
      if (response.data.success) {
        message.success("Document supprime.");
        fetchDocuments(selectedGuest.id);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error deleting document:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la suppression du document"
      );
    }
  };

  const columns: ColumnsType<HotelGuest> = [
    {
      title: "Client",
      key: "guest",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>
            {record.first_name} {record.last_name}
          </Text>
          <Text type="secondary">{record.email || record.phone || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Contact",
      dataIndex: "phone",
      key: "phone",
      render: (_value, record) => record.phone || record.email || "-",
    },
    {
      title: "Nationalite",
      dataIndex: "nationality",
      key: "nationality",
      render: (value: string | null) => value || "-",
    },
    {
      title: "Liste noire",
      dataIndex: "blacklisted",
      key: "blacklisted",
      render: (value: number | boolean | null | undefined) =>
        value ? <Tag color="red">Liste noire</Tag> : <Tag>Autorise</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          <Button type="link" onClick={() => openGuestDrawer(record)}>
            Voir
          </Button>
          {canEditGuest ? (
            <Button type="link" onClick={() => openEditGuest(record)}>
              Modifier
            </Button>
          ) : null}
          {canDeleteGuest ? (
            <Button type="link" danger onClick={() => confirmDeleteGuest(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const documentColumns: ColumnsType<GuestDocument> = [
    {
      title: "Type",
      dataIndex: "document_type",
      key: "document_type",
    },
    {
      title: "Numero",
      dataIndex: "document_number",
      key: "document_number",
    },
    {
      title: "Expiration",
      dataIndex: "expiry_date",
      key: "expiry_date",
      render: (value: string | null) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) =>
        canEditGuest ? (
          <Button type="link" danger onClick={() => handleDeleteDocument(record.id)}>
            Supprimer
          </Button>
        ) : null,
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux clients."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title="Clients"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchGuests}
              loading={loadingGuests}
            >
              Rafraichir
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateGuest}
              disabled={!canCreateGuest}
            >
              Nouveau client
            </Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Form.Item label="Recherche">
                <Input
                  placeholder="Nom, email, telephone"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Liste noire">
                <Select
                  value={filters.blacklisted}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, blacklisted: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "true", label: "Liste noire" },
                    { value: "false", label: "Autorise" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          columns={columns}
          dataSource={guests}
          rowKey="id"
          loading={loadingGuests}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Modal
        title={editingGuest ? "Modifier le client" : "Nouveau client"}
        open={isGuestModalOpen}
        onCancel={() => {
          setIsGuestModalOpen(false);
          setEditingGuest(null);
        }}
        onOk={handleSaveGuest}
        okText="Enregistrer"
      >
        <Form form={guestForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Prenom"
                name="firstName"
                rules={[{ required: true, message: "Prenom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nom"
                name="lastName"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Email" name="email">
                <Input type="email" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Telephone" name="phone">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Genre" name="gender">
                <Select
                  allowClear
                  options={[
                    { value: "male", label: "Homme" },
                    { value: "female", label: "Femme" },
                    { value: "other", label: "Autre" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Date de naissance" name="dateOfBirth">
                <DatePicker style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Nationalite" name="nationality">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Type de document" name="idType">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Numero du document" name="idNumber">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Code postal" name="postalCode">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Ville" name="city">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Pays" name="country">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Adresse" name="address">
            <Input />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Liste noire" name="blacklisted" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title="Profil client"
        width={560}
        open={Boolean(selectedGuest)}
        onClose={() => setSelectedGuest(null)}
      >
        {selectedGuest ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Nom">
                {selectedGuest.first_name} {selectedGuest.last_name}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {selectedGuest.email || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Telephone">
                {selectedGuest.phone || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Naissance">
                {selectedGuest.date_of_birth
                  ? dayjs(selectedGuest.date_of_birth).format("DD/MM/YYYY")
                  : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Nationalite">
                {selectedGuest.nationality || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Liste noire">
                {selectedGuest.blacklisted ? "Oui" : "Non"}
              </Descriptions.Item>
            </Descriptions>
            <Card
              title="Documents"
              extra={
                <Button
                  onClick={handleAddDocument}
                  type="primary"
                  disabled={!canEditGuest}
                >
                  Ajouter
                </Button>
              }
            >
              <Form form={documentForm} layout="vertical">
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Type"
                      name="documentType"
                      rules={[{ required: true, message: "Type requis" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      label="Numero"
                      name="documentNumber"
                      rules={[{ required: true, message: "Numero requis" }]}
                    >
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Date emission" name="issueDate">
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="Date expiration" name="expiryDate">
                      <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item label="Pays emission" name="issuingCountry">
                      <Input />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label="URL fichier" name="documentFile">
                      <Input />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              {documents.length === 0 && !documentsLoading ? (
                <Empty description="Aucun document." />
              ) : (
                <Table
                  columns={documentColumns}
                  dataSource={documents}
                  rowKey="id"
                  pagination={false}
                  loading={documentsLoading}
                  size="small"
                />
              )}
            </Card>
          </Space>
        ) : null}
      </Drawer>
    </Space>
  );
};

export default GuestsTab;
